import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  getAgentConfigValue,
  setAgentConfigValue,
  triggerGerarArtigo,
  CATEGORIA_LABELS,
} from '../../services/blogService';

// ============ Shared modal shell ============
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0, 0, 0, ${p => p.theme?.mode === 'dark' ? .7 : .55});
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`;
const ModalBox = styled.div`
  background: ${p => p.theme.surface};
  color: ${p => p.theme.ink};
  border: 1px solid ${p => p.theme.line};
  border-radius: 14px;
  width: 100%;
  max-width: ${p => p.$wide ? 760 : 580}px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 60px rgba(0,0,0,.4);
  font-family: 'Inter', -apple-system, sans-serif;
  position: relative;
`;
const ModalHead = styled.div`
  padding: 24px 28px 18px;
  border-bottom: 1px solid ${p => p.theme.line};
  h2 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 22px; font-weight: 600;
    color: ${p => p.theme.ink};
    margin: 0 0 4px;
    &::after {
      content: ''; display: block; width: 30px; height: 2px;
      background: ${p => p.theme.gold}; margin-top: 6px;
    }
  }
  p { font-size: 13px; color: ${p => p.theme.muted}; margin: 10px 0 0; line-height: 1.5; }
`;
const ModalBody = styled.div`
  padding: 22px 28px;
  display: flex; flex-direction: column; gap: 16px;
`;
const ModalFooter = styled.div`
  padding: 16px 28px;
  border-top: 1px solid ${p => p.theme.line};
  display: flex; justify-content: ${p => p.$between ? 'space-between' : 'flex-end'};
  gap: 8px; flex-wrap: wrap;
`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 6px;
  font-size: 11px; letter-spacing: 1px; text-transform: uppercase;
  color: ${p => p.theme.muted}; font-weight: 500;

  input, select, textarea {
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 7px;
    padding: 10px 12px;
    font-size: 14px; font-family: inherit;
    letter-spacing: 0; text-transform: none; font-weight: 400;
    outline: none;
    &:focus { border-color: ${p => p.theme.gold}; box-shadow: 0 0 0 3px ${p => p.theme.goldSoft}; }
  }
  textarea { resize: vertical; min-height: 60px; }
  .hint {
    font-size: 11px; letter-spacing: 0; text-transform: none; font-weight: 400;
    color: ${p => p.theme.muted}; margin-top: 2px;
  }
`;
const Btn = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.$primary ? p.theme.ink : p.theme.surface};
  color: ${p => p.$primary ? p.theme.bg : p.theme.ink};
  padding: 9px 18px; border-radius: 6px;
  font-size: 13px; font-weight: 500; letter-spacing: 0.3px;
  cursor: pointer; font-family: inherit;
  transition: all .15s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,.08); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const Msg = styled.div`
  padding: 12px 14px; border-radius: 8px;
  font-size: 13px; line-height: 1.4;
  background: ${p => p.$ok ? 'rgba(47,125,91,.1)' : 'rgba(154,59,59,.1)'};
  color: ${p => p.$ok ? p.theme.ok : p.theme.danger};
  border: 1px solid ${p => p.$ok ? p.theme.ok : p.theme.danger};
`;
const SectionTitle = styled.div`
  font-size: 11px; letter-spacing: 2px;
  color: ${p => p.theme.gold};
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 4px;
`;
const Grid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

// ============ Config Modal ============
const CONFIG_KEYS = [
  'global.score_minimo_aprovacao',
  'global.tamanho_min',
  'global.tamanho_max',
  'identidade.persona',
  'voz.prompt_escrever',
  'estrutura.prompt_critica',
  'seo.prompt',
];

export function ConfigAgenteModalV2({ open, onClose }) {
  const [form, setForm] = useState(Object.fromEntries(CONFIG_KEYS.map(k => [k, ''])));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setMsg(null);
    Promise.all(CONFIG_KEYS.map(k => getAgentConfigValue(k).then(v => [k, v || ''])))
      .then(entries => setForm(prev => ({ ...prev, ...Object.fromEntries(entries) })))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const onChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    const results = await Promise.all(Object.entries(form).map(([k, v]) => setAgentConfigValue(k, v)));
    setSaving(false);
    if (results.every(Boolean)) {
      setMsg({ ok: true, text: 'Configuração salva. Próximos artigos vão usar os novos valores.' });
    } else {
      setMsg({ ok: false, text: 'Algumas chaves falharam ao salvar.' });
    }
  };

  return (
    <Overlay onClick={() => !saving && onClose()}>
      <ModalBox $wide onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <h2>⚙ Configuração do agente</h2>
          <p>Ajuste prompts e parâmetros que controlam a qualidade dos artigos. Mudanças entram em vigor no próximo artigo gerado.</p>
        </ModalHead>
        <ModalBody>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#888', fontSize: 13 }}>Carregando configuração…</div>
          ) : (
            <>
              <SectionTitle>Parâmetros</SectionTitle>
              <Grid3>
                <Field>
                  Score mínimo (0-100)
                  <input type="number" min="0" max="100"
                    value={form['global.score_minimo_aprovacao']}
                    onChange={(e) => onChange('global.score_minimo_aprovacao', e.target.value)} />
                  <span className="hint">Default: 70 · sugerido: 80</span>
                </Field>
                <Field>
                  Tamanho mínimo (palavras)
                  <input type="number" min="500"
                    value={form['global.tamanho_min']}
                    onChange={(e) => onChange('global.tamanho_min', e.target.value)} />
                  <span className="hint">Default: 1200</span>
                </Field>
                <Field>
                  Tamanho máximo
                  <input type="number"
                    value={form['global.tamanho_max']}
                    onChange={(e) => onChange('global.tamanho_max', e.target.value)} />
                  <span className="hint">Default: 1800</span>
                </Field>
              </Grid3>

              <Field>
                Persona — quem é o agente, tom, perspectiva
                <textarea rows={4}
                  value={form['identidade.persona']}
                  onChange={(e) => onChange('identidade.persona', e.target.value)}
                  placeholder="Ex: Você é especialista em Alphaville/Barueri com 10 anos de experiência…" />
              </Field>
              <Field>
                Prompt — Escrever (voz, tom, regras)
                <textarea rows={6}
                  value={form['voz.prompt_escrever']}
                  onChange={(e) => onChange('voz.prompt_escrever', e.target.value)}
                  placeholder="Regras: SEMPRE listar metragem, preço e características de imóveis citados; tom de especialista, não comercial; citar fontes com URL; …" />
              </Field>
              <Field>
                Prompt — Crítica (Quality Gate, E-E-A-T)
                <textarea rows={5}
                  value={form['estrutura.prompt_critica']}
                  onChange={(e) => onChange('estrutura.prompt_critica', e.target.value)} />
              </Field>
              <Field>
                Prompt — SEO (meta tags e slug)
                <textarea rows={4}
                  value={form['seo.prompt']}
                  onChange={(e) => onChange('seo.prompt', e.target.value)} />
              </Field>
              {msg && <Msg $ok={msg.ok}>{msg.text}</Msg>}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Btn onClick={onClose} disabled={saving}>Fechar</Btn>
          <Btn $primary onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Salvando…' : 'Salvar configuração'}
          </Btn>
        </ModalFooter>
      </ModalBox>
    </Overlay>
  );
}

// ============ Keywords Modal ============
const KwRow = styled.div`
  display: flex; gap: 8px;
  input {
    flex: 1;
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 7px;
    padding: 10px 12px;
    font-size: 14px; font-family: inherit;
    outline: none;
    &:focus { border-color: ${p => p.theme.gold}; }
  }
`;
const KwList = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  max-height: 320px; overflow-y: auto;
`;
const KwItem = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 14px;
  background: ${p => p.theme.surfaceAlt};
  border: 1px solid ${p => p.theme.line};
  border-radius: 8px;
  font-size: 14px;
  color: ${p => p.theme.ink};
  button {
    background: transparent; border: none;
    color: ${p => p.theme.muted}; cursor: pointer;
    font-size: 18px; padding: 0 6px;
    &:hover { color: ${p => p.theme.danger}; }
  }
`;

export function KeywordsModalV2({ open, onClose }) {
  const [list, setList] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setMsg(null);
    getAgentConfigValue('seo.keywords_estaticas').then(value => {
      const arr = (value || '').split(',').map(s => s.trim()).filter(Boolean);
      setList(arr);
    }).finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const add = () => {
    const k = input.trim();
    if (!k) return;
    if (list.includes(k)) { setMsg({ ok: false, text: 'Keyword já existe' }); return; }
    setList(prev => [...prev, k]);
    setInput(''); setMsg(null);
  };

  const remove = (kw) => setList(prev => prev.filter(k => k !== kw));

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    const ok = await setAgentConfigValue('seo.keywords_estaticas', list.join(', '));
    setSaving(false);
    setMsg({ ok, text: ok ? 'Keywords salvas. O agente vai usá-las nos próximos artigos.' : 'Erro ao salvar.' });
  };

  return (
    <Overlay onClick={() => !saving && onClose()}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <h2>🔑 Keywords SEO estáticas</h2>
          <p>Palavras-chave que o agente vai considerar em <b>todos</b> os artigos gerados, além das específicas da pauta.</p>
        </ModalHead>
        <ModalBody>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#888', fontSize: 13 }}>Carregando…</div>
          ) : (
            <>
              <KwRow>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                  placeholder="Ex: imóveis alphaville"
                />
                <Btn onClick={add} disabled={!input.trim()}>+ Adicionar</Btn>
              </KwRow>
              {list.length === 0 ? (
                <div style={{ fontSize: 13, color: '#999', fontStyle: 'italic', textAlign: 'center', padding: 20 }}>
                  Nenhuma keyword cadastrada.
                </div>
              ) : (
                <KwList>
                  {list.map((kw, i) => (
                    <KwItem key={i}>
                      <span>{kw}</span>
                      <button onClick={() => remove(kw)} aria-label={`Remover ${kw}`}>×</button>
                    </KwItem>
                  ))}
                </KwList>
              )}
              {msg && <Msg $ok={msg.ok}>{msg.text}</Msg>}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Btn onClick={onClose} disabled={saving}>Fechar</Btn>
          <Btn $primary onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Btn>
        </ModalFooter>
      </ModalBox>
    </Overlay>
  );
}

// ============ Agent Dispatch Modal ============
const TitleList = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  max-height: 380px; overflow-y: auto;
`;
const TitleOption = styled.button`
  text-align: left;
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  border: ${p => p.$active ? `2px solid ${p.theme.gold}` : `1px solid ${p.theme.line}`};
  background: ${p => p.$active ? p.theme.goldSoft : p.theme.surface};
  color: ${p => p.theme.ink};
  cursor: pointer;
  font-size: 14px; line-height: 1.4;
  font-family: inherit;
  font-weight: ${p => p.$active ? 600 : 400};
  transition: all .15s;
  .idx { color: ${p => p.theme.muted}; font-size: 12px; }
  .score { font-size: 12px; font-weight: 600; min-width: 50px; text-align: right; white-space: nowrap; }
`;

export function AgentDispatchModalV2({ open, onClose, onDispatched }) {
  const [step, setStep] = useState('form'); // 'form' | 'titles'
  const [form, setForm] = useState({ tema: '', angulo: '', bairro_alvo: '', categoria: 'guia-bairro', palavra_chave_principal: '' });
  const [titles, setTitles] = useState([]);
  const [scores, setScores] = useState({});
  const [loadingScores, setLoadingScores] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [generating, setGenerating] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [msg, setMsg] = useState(null);

  if (!open) return null;

  const reset = () => {
    setStep('form'); setTitles([]); setScores({}); setSelectedTitle(''); setMsg(null);
  };
  const closeAll = () => { reset(); onClose(); };

  const handleGenerateTitles = async () => {
    if (!form.tema.trim()) { setMsg({ ok: false, text: 'Tema é obrigatório' }); return; }
    setGenerating(true); setMsg(null);
    try {
      const res = await fetch('/api/generate-titles', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao gerar títulos');
      const list = data.titles || [];
      if (list.length === 0) throw new Error('Nenhum título retornado');
      setTitles(list); setSelectedTitle(list[0]); setScores({}); setStep('titles');
      setLoadingScores(true);
      fetch('/api/title-trends', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ titles: list }),
      }).then(r => r.json()).then(({ results }) => {
        const map = {}; (results || []).forEach(r => { map[r.title] = r.score; });
        setScores(map);
      }).catch(() => {}).finally(() => setLoadingScores(false));
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleDispatch = async () => {
    if (!selectedTitle.trim()) { setMsg({ ok: false, text: 'Selecione um título' }); return; }
    setDispatching(true); setMsg(null);
    const res = await triggerGerarArtigo({ ...form, titulo_sugerido: selectedTitle });
    setDispatching(false);
    if (res.ok) {
      if (onDispatched) {
        onDispatched({ runId: res.data.runId || 'sem-id', tema: form.tema, bairro_alvo: form.bairro_alvo, dispatchedAt: Date.now() });
      }
      closeAll();
    } else {
      const parts = [`HTTP ${res.status}`];
      if (res.data?.error) parts.push(res.data.error);
      if (res.data?.message) parts.push(res.data.message);
      setMsg({ ok: false, text: parts.join(' · ') });
    }
  };

  return (
    <Overlay onClick={() => !dispatching && !generating && closeAll()}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        {step === 'form' && (
          <>
            <ModalHead>
              <h2>🤖 Gerar artigo com agente</h2>
              <p>Preencha o tema e o agente vai sugerir 10 títulos otimizados. Você escolhe o melhor e ele gera o artigo completo (≈5 min).</p>
            </ModalHead>
            <ModalBody>
              <Field>
                Tema *
                <input
                  value={form.tema}
                  onChange={(e) => setForm(f => ({ ...f, tema: e.target.value }))}
                  placeholder="Ex: Vale a pena morar no Alphaville Residencial 12?"
                />
              </Field>
              <Field>
                Ângulo (opcional)
                <input
                  value={form.angulo}
                  onChange={(e) => setForm(f => ({ ...f, angulo: e.target.value }))}
                  placeholder="Ex: para famílias com filhos pequenos"
                />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field>
                  Bairro / condomínio
                  <input
                    value={form.bairro_alvo}
                    onChange={(e) => setForm(f => ({ ...f, bairro_alvo: e.target.value }))}
                    placeholder="Ex: Alphaville Residencial 12"
                  />
                </Field>
                <Field>
                  Categoria
                  <select
                    value={form.categoria}
                    onChange={(e) => setForm(f => ({ ...f, categoria: e.target.value }))}
                  >
                    {Object.entries(CATEGORIA_LABELS).map(([slug, label]) => (
                      <option key={slug} value={slug}>{label}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field>
                Palavra-chave principal (opcional)
                <input
                  value={form.palavra_chave_principal}
                  onChange={(e) => setForm(f => ({ ...f, palavra_chave_principal: e.target.value }))}
                  placeholder="Ex: morar alphaville residencial 12"
                />
              </Field>
              {msg && <Msg $ok={msg.ok}>{msg.text}</Msg>}
            </ModalBody>
            <ModalFooter>
              <Btn onClick={closeAll} disabled={generating}>Cancelar</Btn>
              <Btn $primary onClick={handleGenerateTitles} disabled={generating || !form.tema}>
                {generating ? '⏳ Gerando títulos…' : 'Sugerir títulos →'}
              </Btn>
            </ModalFooter>
          </>
        )}

        {step === 'titles' && (
          <>
            <ModalHead>
              <h2>Escolha o título</h2>
              <p>
                {loadingScores ? '⏳ Calculando score SEO…' : '📊 = score SEO/CTR (comprimento, gatilhos, números, especificidade).'}
              </p>
            </ModalHead>
            <ModalBody>
              <TitleList>
                {titles.map((t, i) => {
                  const sc = scores[t];
                  return (
                    <TitleOption key={i} $active={selectedTitle === t} onClick={() => setSelectedTitle(t)}>
                      <span className="idx">{i + 1}.</span>
                      <span style={{ flex: 1 }}>{t}</span>
                      <span className="score" style={{
                        color: sc == null ? '#888' : sc >= 50 ? '#2F7D5B' : sc >= 20 ? '#B8935A' : '#888',
                      }}>
                        {loadingScores && sc == null ? '…' : sc == null ? '—' : `📊 ${sc}`}
                      </span>
                    </TitleOption>
                  );
                })}
              </TitleList>
              <Field>
                Ou edite o título escolhido
                <input value={selectedTitle} onChange={(e) => setSelectedTitle(e.target.value)} />
              </Field>
              {msg && <Msg $ok={msg.ok}>{msg.text}</Msg>}
            </ModalBody>
            <ModalFooter $between>
              <Btn onClick={() => { setStep('form'); setMsg(null); }} disabled={dispatching || generating}>← Voltar</Btn>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn onClick={handleGenerateTitles} disabled={dispatching || generating}>
                  {generating ? '⏳ Gerando…' : '🔄 Gerar novos'}
                </Btn>
                <Btn onClick={closeAll} disabled={dispatching || generating}>Cancelar</Btn>
                <Btn $primary onClick={handleDispatch} disabled={dispatching || generating || !selectedTitle}>
                  {dispatching ? 'Gerando artigo…' : 'Gerar artigo'}
                </Btn>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalBox>
    </Overlay>
  );
}
